// Hands on the scroll: wheel and trackpad, drag with a fling, pinch, keys. Time runs across, so every
// scroll, swipe and drag travels through time. The camera's springs do the smoothing.

import type { Camera } from "./view/camera.ts";

export interface InputHandlers {
  /** A tap or click that did not drag. */
  tap(x: number, y: number): void;
  /** The pointer moved (or is held still) over the scroll. */
  point(x: number, y: number, held: boolean): void;
  leave(): void;
  back(): void;
  /** Something moved, or needs drawing. `scrolled` is false for a plain tap, which moves nothing. */
  changed(scrolled?: boolean): void;
}

export function attachInput(el: HTMLElement, camera: Camera, on: InputHandlers): void {
  el.addEventListener(
    "wheel",
    (ev) => {
      ev.preventDefault();
      const unit = ev.deltaMode === 1 ? 16 : ev.deltaMode === 2 ? camera.plot.width : 1;
      const dx = ev.deltaX * unit,
        dy = ev.deltaY * unit;
      if (ev.ctrlKey || ev.metaKey) {
        // Pinch on a trackpad arrives as ctrl + wheel; a mouse wheel's big notches are tamed.
        camera.zoomAt(Math.exp(-Math.max(-50, Math.min(50, dy)) * 0.0085), ev.clientX, true);
      } else {
        // Either direction travels through time. A mouse wheel's notches glide; a trackpad follows the fingers.
        const d = Math.abs(dx) > Math.abs(dy) ? dx : dy;
        const notched = ev.deltaMode !== 0 || (Math.abs(d) >= 50 && Number.isInteger(d));
        camera.panBy(d, notched);
      }
      on.point(ev.clientX, ev.clientY, false);
      on.changed();
    },
    { passive: false },
  );

  // Safari reports trackpad pinches as gestures, and would otherwise zoom the whole page.
  type Gesture = Event & { scale: number; clientX: number };
  let gestureScale = 1;
  el.addEventListener("gesturestart", (ev) => {
    ev.preventDefault();
    gestureScale = (ev as Gesture).scale;
  });
  el.addEventListener("gesturechange", (ev) => {
    ev.preventDefault();
    const g = ev as Gesture;
    camera.zoomAt(g.scale / gestureScale, g.clientX, false);
    gestureScale = g.scale;
    on.changed();
  });
  el.addEventListener("gestureend", (ev) => ev.preventDefault());

  const pointers = new Map<number, { x: number; y: number }>();
  let drag: { x: number; moved: boolean; samples: { t: number; x: number }[] } | null = null;
  let pinch: { dist: number; mid: number } | null = null;
  const pinchState = () => {
    const [a, b] = [...pointers.values()];
    if (!a || !b) return null;
    return { dist: Math.hypot(a.x - b.x, a.y - b.y), mid: (a.x + b.x) / 2 };
  };

  el.addEventListener("pointerdown", (ev) => {
    el.setPointerCapture(ev.pointerId);
    pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    if (pointers.size === 2) {
      pinch = pinchState();
      drag = null;
    } else if (pointers.size === 1) {
      drag = { x: ev.clientX, moved: false, samples: [{ t: ev.timeStamp, x: ev.clientX }] };
      camera.held = true;
    }
    on.point(ev.clientX, ev.clientY, true);
  });
  el.addEventListener("pointermove", (ev) => {
    if (!pointers.has(ev.pointerId)) {
      on.point(ev.clientX, ev.clientY, false);
      return;
    }
    pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    if (pinch) {
      const now = pinchState();
      if (now && pinch.dist > 0) {
        camera.zoomAt(now.dist / pinch.dist, now.mid, false);
        camera.panBy(pinch.mid - now.mid);
        pinch = now;
        on.changed();
      }
      return;
    }
    on.point(ev.clientX, ev.clientY, true);
    if (!drag) return;
    const dx = ev.clientX - drag.x;
    if (!drag.moved && Math.abs(dx) < 4) return;
    drag.moved = true;
    camera.panBy(-dx);
    drag.x = ev.clientX;
    drag.samples.push({ t: ev.timeStamp, x: ev.clientX });
    if (drag.samples.length > 6) drag.samples.shift();
    on.changed();
  });
  const end = (ev: PointerEvent) => {
    pointers.delete(ev.pointerId);
    camera.held = pointers.size > 0;
    if (pinch) {
      if (pointers.size < 2) pinch = null;
      on.changed();
      return;
    }
    if (!drag) return;
    const tapped = !drag.moved;
    if (tapped) on.tap(ev.clientX, ev.clientY);
    else {
      // Fling: carry on in the direction of the last few moves, slowing to a stop.
      const s = drag.samples;
      const a = s[0],
        b = s[s.length - 1];
      if (a && b && b.t - a.t > 0 && ev.timeStamp - b.t < 80) {
        const v = (b.x - a.x) / ((b.t - a.t) / 1000);
        camera.panBy(-v * 0.3, true);
      }
    }
    drag = null;
    on.point(ev.clientX, ev.clientY, false);
    on.changed(!tapped);
  };
  el.addEventListener("pointerup", end);
  el.addEventListener("pointercancel", end);
  el.addEventListener("pointerleave", () => on.leave());

  window.addEventListener("keydown", (ev) => {
    // Keys pressed in Seek or while reading the notes beside the scroll belong to them.
    if (
      ev.target instanceof HTMLInputElement ||
      (ev.target instanceof Element && ev.target.closest("dialog, .reading"))
    )
      return;
    const mid = camera.plot.left + camera.plot.width / 2;
    const page = camera.plot.width * 0.8;
    switch (ev.key) {
      case "Escape":
        on.back();
        return;
      case "+":
      case "=":
        camera.zoomAt(1.6, mid, true);
        break;
      case "-":
        camera.zoomAt(1 / 1.6, mid, true);
        break;
      case "ArrowRight":
        camera.panBy(page * 0.25, true);
        break;
      case "ArrowLeft":
        camera.panBy(-page * 0.25, true);
        break;
      case "PageDown":
        camera.panBy(page, true);
        break;
      case "PageUp":
        camera.panBy(-page, true);
        break;
      default:
        return;
    }
    ev.preventDefault();
    on.changed();
  });
}
